import React, { useCallback, useEffect, useRef, useState } from "react";
import API from "../services/api";
import { Button, Card, Input, Modal, Select, Space, Switch, Table, Tag, Typography, Tooltip } from "antd";
import RowActions from "../components/RowActions";
import {
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
  PartitionOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import CustomForm from "../components/CustomForm";
import { useSnackbar } from "notistack";
import { getApplicationModules } from "../utils/modules";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

const moduleColors = {
  hrms: "#0e83e2",


};

const FEATURE_TYPE_MODULE = "module";
const FEATURE_TYPE_CUSTOM = "custom";

const featureTypeOptions = [
  { value: FEATURE_TYPE_MODULE, label: "Module" },
  { value: FEATURE_TYPE_CUSTOM, label: "Custom" },
];

const featureTypeLabels = {
  [FEATURE_TYPE_MODULE]: "Module",
  [FEATURE_TYPE_CUSTOM]: "Custom",
};

const FeatureManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [features, setFeatures] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [moduleKeyFilter, setModuleKeyFilter] = useState("");
  const [featureTypeFilter, setFeatureTypeFilter] = useState("");
  const [allFeatures, setAllFeatures] = useState([]);
  const [selectedAppModule, setSelectedAppModule] = useState(null);
  const [moduleKeys, setModuleKeys] = useState([]);
  const [moduleKeyLoading, setModuleKeyLoading] = useState(false);
  const [makeChild, setMakeChild] = useState(false);
  const [featureType, setFeatureType] = useState(FEATURE_TYPE_MODULE);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsFeature, setSettingsFeature] = useState(null);
  const [featureSettings, setFeatureSettings] = useState([]);
  const [featureSettingsLoading, setFeatureSettingsLoading] = useState(false);
  const [newSettingKey, setNewSettingKey] = useState("");
  const [newSettingType, setNewSettingType] = useState();

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  // Guards against out-of-order responses: a slow request for "a" must not
  // overwrite the already-rendered results for "ab".
  const latestRequestRef = useRef(0);
  const isSearchPending = searchTerm !== debouncedSearchTerm;
  const formApplicationModule = selectedAppModule || editingFeature?.application_module || "";

  const moduleKeyOptions = moduleKeys.map((moduleKey) => ({
    value: moduleKey.module_key,
    label: `${moduleKey.display_name} (${moduleKey.module_key})`,
  }));
  const moduleNameOptions = moduleKeys.map((moduleKey) => ({
    value: moduleKey.module_key,
    label: moduleKey.display_name,
  }));

  // Filter options come from the features that actually exist, so the dropdown
  // never offers a key that would return an empty table.
  const moduleKeyFilterOptions = Array.from(
    new Set(allFeatures.map((feature) => feature.module_key).filter(Boolean))
  )
    .sort((a, b) => a.localeCompare(b))
    .map((moduleKey) => ({ value: moduleKey, label: moduleKey }));

  const addMissingModuleKeyOption = (moduleKey) => {
    if (!moduleKey || moduleKeyOptions.some((option) => option.value === moduleKey)) {
      return;
    }
    moduleKeyOptions.push({ value: moduleKey, label: `${moduleKey} (${moduleKey})` });
    moduleNameOptions.push({ value: moduleKey, label: moduleKey });
  };

  if (editingFeature && formApplicationModule === editingFeature.application_module) {
    addMissingModuleKeyOption(editingFeature.module_key);
    addMissingModuleKeyOption(getParentModuleKey(editingFeature));
  }

  const getModuleKeyByDisplayName = (displayName) =>
    moduleKeys.find((moduleKey) => moduleKey.display_name === displayName)?.module_key || undefined;

  // Undefined, not "", when a feature has no parent: both feed a Select, and
  // "" renders as a blank selection where undefined renders the placeholder.
  function getParentModuleKey(feature) {
    if (!feature) return undefined;
    if (feature.parent_module_key) return feature.parent_module_key;
    const parentFeature = allFeatures.find((item) => item.id === feature.parent_id);
    return parentFeature?.module_key || undefined;
  }

  const isModuleFeature = featureType === FEATURE_TYPE_MODULE;

  const featureInitialValues = editingFeature
    ? {
      ...editingFeature,
      feature_type: editingFeature.feature_type || FEATURE_TYPE_MODULE,
      make_child: Boolean(editingFeature.parent_module_key || editingFeature.parent_id),
      parent_module_key: getParentModuleKey(editingFeature),
      child_module_key:
        editingFeature.parent_module_key || editingFeature.parent_id
          ? getModuleKeyByDisplayName(editingFeature.name) || editingFeature.module_key
          : undefined,
    }
    // The selects are left undefined rather than "": antd only renders a
    // placeholder for an empty value, and "" would show as a blank selection.
    : {
      feature_type: FEATURE_TYPE_MODULE,
      make_child: false,
      parent_module_key: undefined,
      module_key: undefined,
      child_module_key: undefined,
    };

  const featureTypeField = {
    name: "feature_type",
    label: "Feature type",
    placeholder: "Select feature type",
    required: true,
    type: "select",
    options: featureTypeOptions,
    width: 12,
    onChange: (val, _event, formik) => {
      setFeatureType(val);
      setMakeChild(false);
      formik?.setFieldValue("name", "");
      formik?.setFieldValue("module_key", undefined);
      formik?.setFieldValue("child_module_key", undefined);
      formik?.setFieldValue("parent_id", undefined);
      formik?.setFieldValue("parent_module_key", undefined);
      formik?.setFieldValue("make_child", false);
    },
  };

  const applicationModuleField = {
    name: "application_module",
    label: "Application",
    placeholder: "Select application",
    required: true,
    type: "select",
    showSearch: true,
    options: getApplicationModules()
      .filter((opt) => opt.value.toUpperCase() !== "")
      .map((opt) => ({ ...opt, label: opt.label.toUpperCase() })),
    width: 12,
    onChange: (val, _event, formik) => {
      setSelectedAppModule(val);
      if (!isModuleFeature) return;
      formik?.setFieldValue("module_key", undefined);
      formik?.setFieldValue("child_module_key", undefined);
      formik?.setFieldValue("name", "");
      formik?.setFieldValue("parent_id", undefined);
      formik?.setFieldValue("parent_module_key", undefined);
    },
  };

  // Both dropdowns read the same catalog, which only loads once an application
  // is picked -- so their placeholder is the only thing that can explain an
  // empty list.
  const catalogReady = Boolean(formApplicationModule);
  const catalogDisabled = !catalogReady || moduleKeyLoading;

  // Where the feature sits: which catalog entry it is, and whether it hangs off
  // a parent. Grouped into one panel because the parent only means anything
  // relative to the module beside it.
  const placementGroup = {
    type: "group",
    name: "placement",
    title: "Placement",
    subtitle: makeChild ? "Nested under an existing feature" : "Sits at the top level",
    extra: {
      name: "make_child",
      label: "Make child",
      type: "switch",
      onChange: (checked, _event, formik) => {
        setMakeChild(checked);
        if (!checked) {
          formik?.setFieldValue("child_module_key", undefined);
          formik?.setFieldValue("parent_id", undefined);
        }
      },
    },
    fields: [
      {
        name: "parent_module_key",
        label: "Parent feature",
        // The gating used to be a fake `""` option, which the form then showed
        // as the *selected* value -- so an untouched parent read "Select
        // application first" as though it were a chosen one.
        placeholder: catalogReady ? "Select parent feature" : "Select application first",
        type: "select",
        showSearch: true,
        options: catalogReady ? moduleNameOptions : [],
        width: 12,
        required: makeChild,
        disabled: catalogDisabled,
      },
      ...(makeChild
        ? [
          {
            name: "child_module_key",
            label: "Feature name",
            placeholder: catalogReady ? "Select feature name" : "Select application first",
            type: "select",
            showSearch: true,
            options: catalogReady ? moduleNameOptions : [],
            width: 12,
            required: true,
            disabled: catalogDisabled,
          },
        ]
        : []),
      {
        name: "module_key",
        label: "Module",
        placeholder: catalogReady ? "Select module" : "Select application first",
        required: true,
        type: "select",
        showSearch: true,
        options: catalogReady ? moduleKeyOptions : [],
        disabled: catalogDisabled,
        width: 12,
      },
    ],
  };

  // Custom features are plain records: a free-text name with no module key or parent linkage.
  const customFeatureFields = [
    {
      name: "name",
      label: "Feature name",
      required: true,
      type: "text",
      minLength: 3,
      placeholder: "Enter feature name",
      width: 12,
    },
  ];

  const featureFields = [
    featureTypeField,
    applicationModuleField,
    ...(isModuleFeature ? [placementGroup] : customFeatureFields),
  ];

  const fetchAllFeatures = useCallback(async () => {
    try {
      const response = await API.get("/features", { params: { limit: 10000 } });
      setAllFeatures(response.data.data || []);
    } catch {
      // Request failures are already captured by the shared API interceptor.
    }
  }, []);

  const fetchModuleKeys = useCallback(
    async (applicationModule) => {
      if (!applicationModule) {
        setModuleKeys([]);
        return;
      }

      setModuleKeyLoading(true);
      try {
        const response = await API.get("/module-keys", {
          params: { application_module: applicationModule },
        });
        setModuleKeys(response.data.data || []);
      } catch (error) {
        enqueueSnackbar("Error fetching module keys", { variant: "error" });
        setModuleKeys([]);
      } finally {
        setModuleKeyLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  const fetchFeatures = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearchTerm,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
        module_key: moduleKeyFilter,
        feature_type: featureTypeFilter,
      };
      const response = await API.get("/features", { params });
      if (requestId !== latestRequestRef.current) return;
      setFeatures(response.data.data || []);
      setTotalCount(
        response.data.meta && typeof response.data.meta.total === "number"
          ? response.data.meta.total
          : Array.isArray(response.data.data)
            ? response.data.data.length
            : 0
      );
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      enqueueSnackbar("Error fetching features", { variant: "error" });
    } finally {
      // Only the newest request may clear the spinner, otherwise a superseded
      // response would hide the loader while a fresh request is still running.
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
  }, [
    page,
    rowsPerPage,
    debouncedSearchTerm,
    sortConfig.key,
    sortConfig.direction,
    moduleKeyFilter,
    featureTypeFilter,
    enqueueSnackbar,
  ]);

  // The full catalog only backs the parent-feature lookup and the module key
  // filter options, so it loads once instead of on every search/filter change.
  useEffect(() => {
    fetchAllFeatures();
  }, [fetchAllFeatures]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  useEffect(() => {
    if (!open) return;
    fetchModuleKeys(formApplicationModule);
  }, [open, formApplicationModule, fetchModuleKeys]);

  // Page resets happen with the change that caused them (see the handlers
  // below). Doing it in an effect fired a second request on every search.

  const closeFeatureDialog = () => {
    setOpen(false);
    setEditingFeature(null);
    setSelectedAppModule(null);
    setModuleKeys([]);
    setMakeChild(false);
    setFeatureType(FEATURE_TYPE_MODULE);
  };

  const handleStatusToggle = async (feature) => {
    const updatedStatus = !feature.is_active;
    try {
      await API.put(`/features/${feature.id}`, { is_active: updatedStatus });
      enqueueSnackbar(`Feature ${updatedStatus ? "activated" : "deactivated"} successfully`, {
        variant: "success",
      });
      fetchFeatures();
    } catch (error) {
      enqueueSnackbar("Error updating feature status", { variant: "error" });
    }
  };

  const handleFormSubmit = async (formData) => {
    setLoading(true);
    try {
      const { id, createdAt, updatedAt, ...payload } = formData;
      const selectedFeatureType = payload.feature_type || FEATURE_TYPE_MODULE;

      if (selectedFeatureType === FEATURE_TYPE_CUSTOM) {
        // A custom feature is just a name - no catalog key, no hierarchy.
        payload.feature_type = FEATURE_TYPE_CUSTOM;
        payload.name = (payload.name || "").trim();
        payload.module_key = null;
        payload.parent_module_key = null;
        payload.parent_id = null;
      } else {
        const selectedParentModuleKey = payload.parent_module_key || "";
        const selectedFeatureModuleKey = payload.module_key || "";
        const selectedChildFeatureName = payload.child_module_key || "";
        const selectedFeatureOption = moduleKeys.find((moduleKey) => moduleKey.module_key === selectedFeatureModuleKey);
        const selectedChildFeatureOption = moduleKeys.find((moduleKey) => moduleKey.module_key === selectedChildFeatureName);

        payload.feature_type = FEATURE_TYPE_MODULE;
        payload.parent_id = null;
        payload.parent_module_key = payload.make_child ? selectedParentModuleKey || null : null;
        payload.module_key = selectedFeatureModuleKey;
        payload.name = payload.make_child
          ? selectedChildFeatureOption?.display_name || selectedChildFeatureName
          : selectedFeatureOption?.display_name || selectedFeatureModuleKey;
      }

      delete payload.make_child;
      delete payload.child_module_key;

      if (editingFeature) {
        await API.put(`/features/${editingFeature.id}`, payload);
        enqueueSnackbar("Feature updated successfully", { variant: "success" });
      } else {
        await API.post("/features", { ...payload, is_active: false });
        enqueueSnackbar("Feature created successfully", { variant: "success" });
      }
      fetchFeatures();
      fetchAllFeatures();
      closeFeatureDialog();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || error.message || "Error saving feature", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!featureToDelete) return;
    try {
      await API.delete(`/features/${featureToDelete.id}`);
      enqueueSnackbar("Feature deleted successfully", { variant: "success" });
      fetchFeatures();
      fetchAllFeatures();
    } catch (error) {
      enqueueSnackbar("Error deleting feature", { variant: "error" });
    } finally {
      setDeleteDialogOpen(false);
      setFeatureToDelete(null);
    }
  };

  const closeSettingsDialog = () => {
    setSettingsOpen(false);
    setSettingsFeature(null);
    setFeatureSettings([]);
    setNewSettingKey("");
    setNewSettingType();
  };

  const openSettingsDialog = async (feature) => {
    setSettingsFeature(feature);
    setSettingsOpen(true);
    setFeatureSettingsLoading(true);
    try {
      const response = await API.get(`/features/${feature.id}/settings`);
      setFeatureSettings(response.data.data || []);
    } catch (error) {
      enqueueSnackbar("Error fetching feature settings", { variant: "error" });
    } finally {
      setFeatureSettingsLoading(false);
    }
  };

  const handleCreateFeatureSetting = async () => {
    if (!settingsFeature || !newSettingKey.trim() || !newSettingType) {
      enqueueSnackbar("Setting key and type are required", { variant: "error" });
      return;
    }

    setFeatureSettingsLoading(true);
    try {
      const response = await API.post(`/features/${settingsFeature.id}/settings`, {
        setting_key: newSettingKey.trim(),
        setting_type: newSettingType,
      });
      setFeatureSettings((previous) => [...previous, response.data.data]);
      setNewSettingKey("");
      setNewSettingType();
      enqueueSnackbar("Feature setting created successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error creating feature setting",
        { variant: "error" }
      );
    } finally {
      setFeatureSettingsLoading(false);
    }
  };

  const handleDeleteFeatureSetting = async (settingId) => {
    if (!settingsFeature) return;
    setFeatureSettingsLoading(true);
    try {
      await API.delete(`/features/${settingsFeature.id}/settings/${settingId}`);
      setFeatureSettings((previous) => previous.filter((setting) => setting.id !== settingId));
      enqueueSnackbar("Feature setting deleted successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Error deleting feature setting", { variant: "error" });
    } finally {
      setFeatureSettingsLoading(false);
    }
  };

  const columns = [
    {
      title: "Feature Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: sortConfig.key === "name" ? `${sortConfig.direction}end` : null,
      width: 200,
      ellipsis: { showTitle: true },
    },
    {
      title: "Type",
      dataIndex: "feature_type",
      key: "feature_type",
      width: 110,
      render: (value) => {
        const type = value || FEATURE_TYPE_MODULE;
        return (
          <Tag color={type === FEATURE_TYPE_MODULE ? "geekblue" : "purple"} style={{ border: 0 }}>
            {featureTypeLabels[type] || type}
          </Tag>
        );
      },
    },
    {
      title: "Module Key",
      dataIndex: "module_key",
      key: "module_key",
      sorter: true,
      sortOrder: sortConfig.key === "module_key" ? `${sortConfig.direction}end` : null,
      width: 190,
      ellipsis: { showTitle: true },
      render: (value) => value || "-",
    },
    {
      title: "Parent Feature",
      key: "parent",
      width: 170,
      ellipsis: { showTitle: true },
      render: (_, feature) => {
        if (feature.parent_module_key) {
          // The API returns the raw key (e.g. "hr_management"); show the human
          // readable feature name when we have it loaded.
          const parentByKey = allFeatures.find((item) => item.module_key === feature.parent_module_key);
          return parentByKey ? parentByKey.name : feature.parent_module_key;
        }
        const parentFeature = allFeatures.find((item) => item.id === feature.parent_id);
        return parentFeature ? parentFeature.name : "None";
      },
    },
    // {
    //   title: "Description",
    //   dataIndex: "description",
    //   key: "description",
    //   width: 220,
    //   ellipsis: { showTitle: true },
    //   render: (value) => value || "-",
    // },
    // {
    //   title: "Application Module",
    //   dataIndex: "application_module",
    //   key: "application_module",
    //   sorter: true,
    //   sortOrder: sortConfig.key === "application_module" ? `${sortConfig.direction}end` : null,
    //   width: 170,
    //   render: (value) => (
    //     <Tag color={moduleColors[value] || moduleColors.default} style={{ border: 0 }}>
    //       {value}
    //     </Tag>
    //   ),
    // },
    {
      title: "Status",
      key: "status",
      width: 150,
      render: (_, feature) => (
        <Space>
          <Switch checked={feature.is_active} onChange={() => handleStatusToggle(feature)} disabled={loading} />
          <Tag color={feature.is_active ? "green" : "default"}>{feature.is_active ? "Active" : "Inactive"}</Tag>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 90,
      fixed: "right",
      render: (_, feature) => (
        <RowActions
          disabled={loading}
          items={[
            {
              key: "settings",
              label: "Feature Settings",
              icon: <SettingOutlined />,
              onClick: () => openSettingsDialog(feature),
            },
            {
              key: "edit",
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () => {
                setEditingFeature(feature);
                setSelectedAppModule(feature.application_module);
                setFeatureType(feature.feature_type || FEATURE_TYPE_MODULE);
                setMakeChild(Boolean(feature.parent_module_key || feature.parent_id));
                setOpen(true);
              },
            },
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => {
                setFeatureToDelete(feature);
                setDeleteDialogOpen(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  const hasActiveFilters = Boolean(searchTerm || moduleKeyFilter || featureTypeFilter);

  // Every filter change goes back to page 1, otherwise a narrowed result set
  // can land on a page that no longer exists and render an empty table.
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleModuleKeyFilterChange = (value) => {
    setModuleKeyFilter(value);
    setPage(0);
  };

  // A custom feature never carries a module key -- createFeature nulls it out --
  // so the two filters can only ever intersect to an empty table. The dropdown
  // is hidden rather than disabled, and a key already chosen is dropped with
  // it, so what the table shows always matches the filters still on screen.
  const showModuleKeyFilter = featureTypeFilter !== FEATURE_TYPE_CUSTOM;

  const handleFeatureTypeFilterChange = (value) => {
    setFeatureTypeFilter(value);
    if (value === FEATURE_TYPE_CUSTOM) {
      setModuleKeyFilter("");
    }
    setPage(0);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setModuleKeyFilter("");
    setFeatureTypeFilter("");
    setPage(0);
  };

  const emptyState = (
    <div style={{ padding: 48, textAlign: "center" }}>
      <PartitionOutlined style={{ fontSize: 56, color: "rgba(100, 116, 139, 0.45)" }} />
      <Typography.Title level={4} style={{ marginTop: 16 }}>
        No features found
      </Typography.Title>
      <Typography.Text type="secondary">
        {hasActiveFilters ? "Clear filters to see all features." : "Create the first feature to get started."}
      </Typography.Text>
      <div style={{ marginTop: 20 }}>
        {hasActiveFilters ? (
          <Button icon={<ClearOutlined />} onClick={clearFilters}>
            Clear filters
          </Button>
        ) : (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            Add feature
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Card className="app-page-card" variant="borderless" styles={{ body: { padding: 24 } }}>
      <div className="page-toolbar">
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            Feature Management
          </Typography.Title>
        </div>
        <Space>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchFeatures();
              fetchAllFeatures();
            }}
            loading={loading}
          />
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setOpen(true)}
            disabled={loading}
          >
            Add Feature
          </Button>
        </Space>
      </div>

      <Space style={{ width: "100%", marginBottom: 20 }} wrap>
        {/*
          Deliberately not disabled while loading: the table already shows a
          spinner, and disabling the input mid-request drops keyboard focus
          and swallows keystrokes as you type.
        */}
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined />}
          suffix={isSearchPending ? <LoadingOutlined /> : <span />}
          placeholder="Search features..."
          value={searchTerm}
          onChange={handleSearchChange}
          style={{ width: 300 }}
        />
        {showModuleKeyFilter && (
          <Select
            value={moduleKeyFilter}
            onChange={handleModuleKeyFilterChange}
            style={{ width: 240 }}
            placeholder="Module Key"
            showSearch
            optionFilterProp="label"
            options={[{ value: "", label: "All Module Keys" }, ...moduleKeyFilterOptions]}
          />
        )}
        <Select
          value={featureTypeFilter}
          onChange={handleFeatureTypeFilterChange}
          style={{ width: 200 }}
          placeholder="Feature Type"
          options={[{ value: "", label: "All Types" }, ...featureTypeOptions]}
        />
        {hasActiveFilters && (
          <Button icon={<ClearOutlined />} onClick={clearFilters}>
            Clear
          </Button>
        )}
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={features}
        loading={loading}
        locale={{ emptyText: emptyState }}
        size="medium"
        scroll={{ x: 1200 }}
        pagination={{
          current: page + 1,
          pageSize: rowsPerPage,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 25],
        }}
        // Paging and sorting are both driven from here. They must not use
        // pagination.onChange as well: the table reports the active sorter on
        // every change, so a paginate would also look like a sort and reset
        // the page back to 1 (the name column is always sorted by default).
        onChange={(tablePagination, _filters, sorter, extra) => {
          if (extra?.action === "sort") {
            const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            const nextKey = activeSorter?.order ? activeSorter.field : "name";
            const nextDirection = activeSorter?.order === "descend" ? "desc" : "asc";
            if (nextKey !== sortConfig.key || nextDirection !== sortConfig.direction) {
              setSortConfig({ key: nextKey, direction: nextDirection });
              setPage(0);
            }
            return;
          }

          if (extra?.action === "paginate") {
            const nextPageSize = tablePagination?.pageSize ?? rowsPerPage;
            // A different page size renumbers the pages, so start over at 1.
            if (nextPageSize !== rowsPerPage) {
              setRowsPerPage(nextPageSize);
              setPage(0);
              return;
            }
            setPage((tablePagination?.current ?? 1) - 1);
          }
        }}
      />

      <Modal
        open={open}
        onCancel={closeFeatureDialog}
        title={
          <div className="modal-heading">
            <span className="modal-heading__icon">
              {editingFeature ? <EditOutlined /> : <PlusOutlined />}
            </span>
            <div>
              <div className="modal-heading__title">
                {editingFeature ? "Edit Feature" : "Add New Feature"}
              </div>
              <div className="modal-heading__subtitle">
                {isModuleFeature
                  ? "Choose an application, then place the feature in its hierarchy."
                  : "Give the feature a name. Custom features carry no module or parent."}
              </div>
            </div>
          </div>
        }
        footer={null}
        width={680}
        centered
        destroyOnHidden
        styles={{
          body: {
            padding: "20px 24px",
          },
        }}
      >
        <CustomForm
          fields={featureFields}
          initialValues={featureInitialValues}
          formResetKey={editingFeature?.id || "create"}
          onSubmit={handleFormSubmit}
          loading={loading || moduleKeyLoading}
          buttonText={editingFeature ? "Update Feature" : "Create Feature"}
          footerBordered
          onCancel={closeFeatureDialog}
        />
      </Modal>
      <Modal
        open={deleteDialogOpen}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setFeatureToDelete(null);
        }}
        onOk={handleDeleteConfirm}
        okText="Delete"
        okButtonProps={{ danger: true, loading }}
        title="Confirm delete"
      >
        <Typography.Text>
          Are you sure you want to delete feature <strong>{featureToDelete?.name}</strong>?
        </Typography.Text>
      </Modal>
      <Modal
        open={settingsOpen}
        onCancel={closeSettingsDialog}
        footer={null}
        title={`Feature Settings${settingsFeature ? ` — ${settingsFeature.name}` : ""}`}
        width={700}
        destroyOnHidden
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Typography.Text strong>Key</Typography.Text>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <Input
                value={newSettingKey}
                onChange={(event) => setNewSettingKey(event.target.value)}
                placeholder="Enter setting key (for example: max_users)"
                disabled={featureSettingsLoading}
                style={{ flex: "1 1 280px" }}
              />
              <Select
                value={newSettingType}
                onChange={setNewSettingType}
                placeholder="Select setting type"
                style={{ flex: "0 1 180px", minWidth: 180 }}
                disabled={featureSettingsLoading}
                options={[
                  { value: "boolean", label: "Boolean" },
                  { value: "number", label: "Number" },
                  { value: "string", label: "String" },
                  { value: "json", label: "JSON" },
                ]}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateFeatureSetting}
                loading={featureSettingsLoading}
              >
                Add
              </Button>
            </div>
          </Space>

          <Table
            rowKey="id"
            size="small"
            loading={featureSettingsLoading}
            pagination={false}
            dataSource={featureSettings}
            locale={{ emptyText: "No settings created for this feature" }}
            columns={[
              { title: "Key", dataIndex: "setting_key", key: "setting_key" },
              {
                title: "Type",
                dataIndex: "setting_type",
                key: "setting_type",
                render: (value) => <Tag color="blue">{value}</Tag>,
              },
              {
                title: "Actions",
                key: "actions",
                align: "right",
                render: (_, setting) => (
                  <Tooltip title="Delete setting">
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteFeatureSetting(setting.id)}
                      disabled={featureSettingsLoading}
                    />
                  </Tooltip>
                ),
              },
            ]}
          />
        </Space>
      </Modal>
    </Card>
  );
};

export default FeatureManagement;
